import {
  FormDefinition,
  FormElementBase,
  FormElementRuleType,
  FormElementType,
  FormResponse,
} from "~/api";
import { FormResponseAnswerMap } from "./types";
import { getIsRuleApplicable } from "./rulesEngine";
import { nonQuestionElements } from "~/NewFormBuilder/types/NonQuestionElements";
import { emptyGuid } from "~/utils/constants";
import { isRepeaterAnswerValue } from "~/utils/typeGuards";

/**
 * Builds an answer map from a form response's answers, keyed by "questionCode-repeaterGroupGuid".
 * Same logic as setAnswers in useFormResponse.ts.
 */
function buildAnswerMap(
  formDefinition: FormDefinition,
  formResponse: FormResponse,
): FormResponseAnswerMap {
  return Object.fromEntries(
    (formResponse.answers ?? []).map((a) => {
      const repeaterGuid = a.repeatableGroupGuid ?? emptyGuid;
      const key =
        formDefinition.components.find((c) => c.guid === a.formElementGuid)
          ?.questionCode +
        "-" +
        repeaterGuid;

      return [
        key,
        {
          ...a,
          answerValue: JSON.parse(a.answerValue),
        },
      ];
    }),
  );
}

/**
 * Pure function that computes the number of unanswered required questions in a form.
 *
 * Mirrors the logic in useFormResponse.getUnansweredQuestions() but without
 * reactive overhead — suitable for one-shot status checks outside the form renderer.
 */
export function getUnansweredQuestionCount(
  formDefinition: FormDefinition,
  formResponse: FormResponse,
): number {
  const answerMap = buildAnswerMap(formDefinition, formResponse);

  const isHidden = (element: FormElementBase): boolean => {
    const elementRules = formDefinition.rules.filter(
      (r) =>
        r.ruleType === FormElementRuleType.HIDDEN &&
        (r.elementGuid === element.guid ||
          r.elementGuid === element.parentGuid),
    );
    return elementRules
      .map((rule) =>
        getIsRuleApplicable(
          rule,
          answerMap,
          formDefinition.components,
          formResponse,
        ),
      )
      .some(Boolean);
  };

  const isReadOnly = (element: FormElementBase): boolean => {
    if (element.properties.isReadOnly) return true;
    const elementRules = formDefinition.rules.filter(
      (r) =>
        r.ruleType === FormElementRuleType.READ_ONLY &&
        (r.elementGuid === element.guid ||
          r.elementGuid === element.parentGuid),
    );
    return elementRules
      .map((rule) =>
        getIsRuleApplicable(
          rule,
          answerMap,
          formDefinition.components,
          formResponse,
        ),
      )
      .some(Boolean);
  };

  const hasValidAnswer = (
    component: FormElementBase,
    repeaterGroupGuid: string = emptyGuid,
  ): boolean => {
    const key = `${component.questionCode}-${repeaterGroupGuid}`;
    const answerValue = answerMap[key]?.answerValue;

    if (component.type === FormElementType.FILE_UPLOAD) {
      return Array.isArray(answerValue) && answerValue.length > 0;
    }

    return (
      answerValue !== undefined && answerValue !== "" && answerValue !== null
    );
  };

  const isUnanswered = (
    component: FormElementBase,
    repeaterGuid?: string,
  ): boolean => {
    return (
      !nonQuestionElements.includes(component.type) &&
      !component.isArchived &&
      !isReadOnly(component) &&
      !isHidden(component) &&
      !hasValidAnswer(component, repeaterGuid) &&
      !component.properties?.isOptional
    );
  };

  // Identify repeaters and their children
  const repeaters = formDefinition.components.filter(
    (c) => c.type === FormElementType.REPEATER,
  );
  const repeaterGuids = new Set(repeaters.map((r) => r.guid));
  const componentsInRepeaters = formDefinition.components.filter((c) =>
    repeaterGuids.has(c.parentGuid),
  );
  const componentsInRepeatersSet = new Set(
    componentsInRepeaters.map((c) => c.guid),
  );

  let count = 0;

  // Check top-level (non-repeater) components
  for (const component of formDefinition.components) {
    if (
      !componentsInRepeatersSet.has(component.guid) &&
      isUnanswered(component)
    ) {
      count++;
    }
  }

  // Check components inside repeaters
  for (const repeater of repeaters) {
    const repeaterKey = `${repeater.questionCode}-${emptyGuid}`;
    const repeaterAnswer = answerMap[repeaterKey]?.answerValue;

    if (!repeaterAnswer || !isRepeaterAnswerValue(repeaterAnswer)) {
      continue;
    }

    const groupGuids: string[] = repeaterAnswer?.repeatableGroupGuids ?? [];
    const children = componentsInRepeaters.filter(
      (c) => c.parentGuid === repeater.guid,
    );

    for (const guid of groupGuids) {
      for (const component of children) {
        if (isUnanswered(component, guid)) {
          count++;
        }
      }
    }
  }

  return count;
}
